/**
 * @file catalog.component.tsx
 * @description Основной компонент каталога для отображения и управления списками сущностей в Task Manager приложении. Предоставляет полнофункциональный интерфейс для просмотра, поиска, фильтрации и управления различными типами данных (задачи, пользователи, проекты и др.) с поддержкой пагинации, группировки, модальных окон для создания/редактирования записей, системы фильтров с сохранением пользовательских настроек, обработки ошибок API и интеграции с системой разрешений для контроля доступа к операциям CRUD.
 * @author AI-Generated | Human-Reviewed
 * @created 2025-01-15
 * @updated 2025-09-22
 * @dependencies ['@components/ui/layout/page-modal', '@src/features/object-form', './components', '@hooks/use-modals', 'react', '@components/ui', '@api/actions', '@components/ui/custom/buttons', '@src/types', '@components/ui/indicators/error-list', 'axios', '@features/filter', './context/search-text-context', '@utils/form-object-helper', '@utils/error-helper', '@utils/data-helper', '../table']
 * @related-files ['src/features/catalog/catalog.provider.tsx', 'src/features/catalog/context/search-text-context.tsx', 'src/features/catalog/components', 'src/api/actions/catalog.ts', 'src/utils/form-object-helper.ts']
 */

import { PageModal } from '@components/ui/layout/page-modal';
import { ObjectForm } from '@src/features/object-form';
import { Header, Body, GroupSlider } from './components';
import { useModalQueue } from '@hooks/use-modals';
import { useState, useEffect, useRef, useMemo } from 'react';
import { Loader } from '@components/ui';
import { CatalogService } from '@api/actions';
import { AddRowButton } from '@components/ui/custom/buttons/add-row-button';
import { filterComponent } from '@features/filter/types';
import { TBaseObjectDto } from '@src/types';
import { ErrorList, ErrorListHandle } from '@components/ui/indicators/error-list';
import axios, { AxiosError, AxiosResponse } from 'axios';
import {
    getActiveFilters,
    getInitialValuesFromFilterControls,
    prepareFilterControlsToFetch,
    prepareFilterFormToFetch,
    FilterModal,
    ToolbarTags as FilterTagLine
} from '@features/filter';
import { useSavedFilters } from '@features/filter';
import { useSearchText } from './context/search-text-context';
import { getObjectEntity } from '@utils/form-object-helper';
import { setErrors } from '@utils/error-helper';
import { updateTableRow } from '@utils/data-helper';
import { ITableHandlers } from '../table';
import { ITableItemDTO } from '@src/types/dto';
import { IGroupItem, TCheckedItems } from '@src/types/tree-components';
import { ICatalogPage } from '../../types/pages';

interface ICatalog {
    container?: HTMLDivElement;
    type: string;
    currentItems?: ITableItemDTO[];
    onRowClick?: (row: ITableItemDTO, column?: string) => void;
    stepsCount?: number;
    onClose?: () => void;
}

export const Catalog = ({ container, type, onRowClick, onClose, currentItems = [], stepsCount = 1 }: ICatalog) => {
    const pageSize = 40;
    const [entity, setEntity] = useState<ICatalogPage | undefined>(undefined);
    const { getFilters, saveFilters } = useSavedFilters(type);
    //TODO: добавить тип для строк
    const [rows, setRows] = useState<ITableItemDTO[] | undefined>(undefined);
    const [groups, setGroups] = useState<IGroupItem[] | undefined>(undefined);
    const [addedItem, setAddedItem] = useState<TBaseObjectDto | undefined>(undefined);
    const [lastItemsPackCount, setLastItemsPackCount] = useState<number>(0);
    const { searchText } = useSearchText();
    const [isShow, setIsShow] = useState<boolean>(true);
    //TODO: переименовать типы через PascalCase
    const [filterControls, setFilterControls] = useState<filterComponent[]>(
        entity?.getFilterControls ? entity.getFilterControls() : []
    );
    const activeFilters = useMemo(() => getActiveFilters(filterControls), [filterControls]);
    const [isShowFilterModal, setIsShowFilterModal] = useState<boolean>(false);
    const errorListRef = useRef<ErrorListHandle | null>(null);
    const groupFilterRef = useRef<TCheckedItems>([]);
    const rowsRef = useRef<TBaseObjectDto[] | undefined>(rows);
    const tableRef = useRef<ITableHandlers | null>(null);
    const pageNumberRef = useRef<number>(1);
    //TODO: type PascalCase
    const filterControlsRef = useRef<filterComponent[]>(filterControls);

    const currentStepRef = useRef(1);
    const columns = useMemo(() => entity ? entity.columns : [], [entity]);
    const form = useMemo(() => entity?.form, [entity]);
    const tabs = useMemo(() => entity?.tabs, [entity]);
    const title = useMemo(() => entity?.title || 'Выбор из списка', [entity]);
    const isCanAddGroups = useMemo(
        () => (entity?.isCanAddGroups !== undefined ? entity.isCanAddGroups : false),
        [entity]
    );

    const closePageHandler = useModalQueue(() => {
        setIsShow(false);
        onClose && onClose();
    }, type);
    const [modalContainer, setModalContainer] = useState<HTMLDivElement | undefined>(container);
    const [currentItem, setCurrentItem] = useState<TBaseObjectDto | undefined>(undefined);

    useEffect(() => {
        if (!entity) {
            return;
        }

        async function tryFecthSavedFilters() {
            const savedFilters: TBaseObjectDto = await getFilters();
            const newFilterControls = entity?.getFilterControls ? entity.getFilterControls(savedFilters) : [];
            setFilterControls(newFilterControls);
            fetchData(savedFilters, true, newFilterControls);
        }

        pageNumberRef.current = 1;
        setRows(undefined);
        setCurrentItem(undefined);
        tryFecthSavedFilters();
    }, [entity]);

    useEffect(() => {
        return () => closePageHandler();
    }, []);

    useEffect(() => {
        const entity = getObjectEntity(type) as ICatalogPage;
        setEntity(entity);
    }, [type]);

    useEffect(() => {
        rowsRef.current = rows;
    }, [rows]);

    useEffect(() => {
        filterControlsRef.current = filterControls;
    }, [filterControls]);

    useEffect(() => {
        if (rows) {
            searchText;
            pageNumberRef.current = 1;
            const timeOutId = setTimeout(() => fetchData(), 500);

            return () => clearTimeout(timeOutId);
        }
    }, [searchText]);

    const fetchData = async (
        filters?: TBaseObjectDto,
        isGetGroups: boolean = false,
        _filterControls: filterComponent[] = filterControlsRef.current
    ) => {
        const pageNumber = pageNumberRef.current;
        const filterGroups = groupFilterRef.current;
        let filterItems = filters
            ? prepareFilterFormToFetch(filters, _filterControls)
            : prepareFilterControlsToFetch(_filterControls);

        if (filterGroups.length > 0) {
            filterItems = [...filterItems, { field: 'group', comparison: 'in', values: filterGroups }];
        }

        if (currentItems?.length) {
            const filterIds = currentItems.map((currentItem: ITableItemDTO) => currentItem.$item?.id);

            if (filterIds?.length) {
                filterItems = [
                    ...filterItems,
                    {
                        field: 'id',
                        comparison: 'notIn',
                        values: filterIds
                    }
                ];
            }
        }

        const response: AxiosResponse | AxiosError = await CatalogService.getList(
            entity?.type || type,
            {
                searchText,
                filterItems
            },
            { pageSize, pageNumber, isGetGroups }
        );

        if (axios.isAxiosError(response)) {
            errorListRef.current && setErrors(response, errorListRef.current);
            return;
        }

        if (response.data.groups) {
            setGroups(response.data.groups);
        }

        //TODO: добавить проверку на последний пакет
        // Объединяем текущие данные с новыми и удаляем дубли по id
        if (pageNumber !== 1) {
            const fetchedRows = response.data.objects || [];
            const existingRows = rowsRef.current || [];
            const combinedRows = [...existingRows, ...fetchedRows];
            const uniqueRows = Array.from(new Map(combinedRows.map((item: ITableItemDTO) => [item.id, item])).values());

            if (fetchedRows.length > 1 && rowsRef.current && uniqueRows.length === rowsRef.current.length) {
                pageNumberRef.current++;
                fetchData(filters, isGetGroups);
                return;
            }

            setRows(uniqueRows);
        } else {
            setRows(response.data.objects);
        }

        // tableRef.current?.scroll(0, 0);
        setIsShow(true);
        pageNumberRef.current++;
    };

    const onAddNewRecord = (newItem: TBaseObjectDto) => {
        setAddedItem(newItem);
        pageNumberRef.current = 1;
        fetchData();
    };

    const onTableDataUpdated = () => {
        if (addedItem) {
            tableRef.current?.scrollTo(addedItem.id);
            tableRef.current?.selectRow(addedItem.id);
            setAddedItem(undefined);
        }

        if (lastItemsPackCount) {
            tableRef.current?.scroll(0, lastItemsPackCount * 30);
            setLastItemsPackCount(0);
        }
    };

    useEffect(() => {
        setModalContainer(container || (document.getElementById('MainLayout') as HTMLDivElement));
    }, []);

    const rowClickHandler = (item: ITableItemDTO) => setCurrentItem({ id: item.id, $type: item.$type });

    const onRowChange = (newItemValue: ITableItemDTO) => {
        if (!rows || !newItemValue) {
            return;
        }
        
        const newRows = updateTableRow(rows, newItemValue);
        setRows(newRows);
    };

    const onAddNewGroup = () => {
        fetchData(undefined, true);
    };

    if (!modalContainer) {
        return;
    }

    return (
        <PageModal container={modalContainer} closePageHandler={closePageHandler} isOpen={isShow}>
            <div className="catalog gap-default h-full w-full relative">
                <div className="catalog__wrapper absolute w-full h-full flex flex-col pr-[3.1756vmin]">
                    <Header
                        title={title}
                        closePageHandler={closePageHandler}
                        filterControls={filterControls}
                        onFilterButtonClick={() => setIsShowFilterModal(true)}
                        isShowFilterModal={isShowFilterModal}
                        isCanShowModal={!isShowFilterModal && !Boolean(currentItem)}
                    />

                    {activeFilters.length > 0 && (
                        <FilterTagLine
                            filters={activeFilters}
                            onFilterClick={() => setIsShowFilterModal(true)}
                            onClearFilters={(filterName: string | undefined) => {
                                let newFilterControls: filterComponent[] = [];

                                if (filterName) {
                                    newFilterControls = filterControls.map((filterControl: filterComponent) => {
                                        if (filterControl.name !== filterName) {
                                            return filterControl;
                                        }

                                        return { ...filterControl, value: null };
                                    });
                                } else {
                                    newFilterControls = entity?.getFilterControls && entity.getFilterControls() || [];
                                }

                                setFilterControls(newFilterControls);
                                pageNumberRef.current = 1;
                                fetchData(getInitialValuesFromFilterControls(newFilterControls));
                                saveFilters(newFilterControls);
                            }}
                        />
                    )}

                    <div className="flex flex-1 overflow-x-hidden gap-default w-full h-full">
                        {/* {groups && (
                            <GroupFilter
                                groups={groups}
                                onCheckItem={(groupList: TBaseObjectDto[]) => {
                                    groupFilterRef.current = groupList;
                                    pageNumberRef.current = 1;
                                    fetchData();
                                    saveFilters(filterControlsRef.current);
                                }}
                            />
                        )} */}

                        {rows && entity ? (
                            <Body
                                rows={rows}
                                columns={columns}
                                onScrollToLast={() => fetchData()}
                                onRowClick={(clickedRow: ITableItemDTO) => {
                                    if (currentStepRef.current < stepsCount) {
                                        const newEntity = getObjectEntity(
                                            clickedRow.entity || clickedRow.$item?.$type || 'default'
                                        ) as ICatalogPage;
                                        setEntity(newEntity);
                                        currentStepRef.current++;
                                        return;
                                    }

                                    if (onRowClick) {
                                        onRowClick(clickedRow, undefined);
                                        closePageHandler();
                                        return;
                                    }

                                    rowClickHandler && rowClickHandler(clickedRow);
                                }}
                                tableRef={tableRef}
                                onTableDataUpdated={onTableDataUpdated}
                                isCanAddGroups={isCanAddGroups}
                            />
                        ) : (
                            <Loader />
                        )}
                    </div>
                </div>

                {Boolean(currentItem) && (
                    <ObjectForm
                        initialItem={currentItem}
                        container={container}
                        type={entity?.type || type}
                        tabs={tabs}
                        form={form}
                        onClose={() => setCurrentItem(undefined)}
                        onChange={onRowChange}
                    />
                )}

                {currentStepRef.current >= stepsCount && (
                    <AddRowButton
                        type={entity?.type || type}
                        onDataUpdate={(newItem: TBaseObjectDto) => onAddNewRecord(newItem)}
                        isAdditional={isCanAddGroups}
                        currentItems={currentItems}
                    />
                )}

                {groups && entity && (
                    <GroupSlider
                        groups={groups}
                        type={entity.parentType || 'undefined parentType on catalog.component.tsx'}
                        updateDataHandler={onAddNewGroup}
                        onCheckItem={(groupList: TCheckedItems) => {
                            groupFilterRef.current = groupList;
                            pageNumberRef.current = 1;
                            fetchData();
                            saveFilters(filterControlsRef.current);
                        }}
                    />
                )}

                <ErrorList ref={errorListRef} />

                {isShowFilterModal && (
                    <FilterModal
                        container={modalContainer}
                        isShow={isShowFilterModal}
                        setIsShow={setIsShowFilterModal}
                        filterControls={filterControls}
                        applyNewFilterControls={(filters: filterComponent[]) => {
                            setFilterControls(filters);
                            saveFilters(filters);
                        }}
                        fetchDataHadler={(filters: filterComponent[]) => {
                            pageNumberRef.current = 1;
                            fetchData(filters);
                        }}
                        itemType={entity?.type || type}
                    />
                )}
            </div>
        </PageModal>
    );
};
